import moment from "moment-timezone";
import { toast } from "react-toastify";
import { APIError } from "../../core/utils";

// Dealer API admin page (routes/dealer_api_admin.php in ctb_lumino, docs/api/admin/dealer-api.md).

export const DEALER_API_TABS = [
  { id: "requests", label: "Access requests" },
  { id: "keys", label: "Keys" },
  { id: "pushes", label: "Push activity" },
  { id: "activity", label: "Activity" },
  { id: "reference", label: "Reference data" },
  { id: "docs", label: "Documentation" },
];

// GET /reference/{resource}. Dealers read the same lists from their own endpoint; this tab exists
// so support can quote an id without a dealer key. `filter` is the query the resource accepts.
export const REFERENCE_RESOURCES = [
  { value: "product_types", label: "Product types", filter: null },
  { value: "brands", label: "Brands", filter: "product_type" },
  { value: "models", label: "Models", filter: "brand" },
  { value: "fuels", label: "Fuels", filter: "product_type" },
  { value: "gearboxes", label: "Gearboxes", filter: "product_type" },
  { value: "body_types", label: "Body types", filter: "product_type" },
  { value: "colors", label: "Colours", filter: "product_type" },
  { value: "interiors", label: "Interiors", filter: "product_type" },
  { value: "euro_standards", label: "Euro standards", filter: null },
  { value: "equipment", label: "Equipment", filter: "product_type" },
  { value: "tire_sizes", label: "Tyre sizes", filter: "product_type" },
  { value: "engines", label: "Engines", filter: "product_type" },
  { value: "countries", label: "Countries", filter: null },
];

export const REFERENCE_LANGUAGES = [
  { name: "English", value: "en" },
  { name: "Français", value: "fr" },
  { name: "Nederlands", value: "nl" },
  { name: "Deutsch", value: "de" },
  { name: "Español", value: "es" },
];

export const ACCESS_STATUSES = [
  { name: "Pending", value: "pending" },
  { name: "Approved", value: "approved" },
  { name: "Rejected", value: "rejected" },
  { name: "Revoked", value: "revoked" },
];

export const BATCH_STATUSES = [
  { name: "Accepted", value: "accepted" },
  { name: "Processing", value: "processing" },
  { name: "Completed", value: "completed" },
  { name: "Completed with errors", value: "completed_with_errors" },
  { name: "Failed", value: "failed" },
];

export const ACTIVITY_PERIODS = [
  { name: "Last 24 hours", value: "24h" },
  { name: "Last 7 days", value: "7d" },
  { name: "Last 30 days", value: "30d" },
];

// The API caps per_page at 100.
export const PER_PAGE_OPTIONS = [10, 25, 50, 100];

// Status slug → pill class (style/DealerApiWrapper.js).
const PILL = {
  pending: "pill-grey",
  accepted: "pill-grey",
  processing: "pill-yellow",
  approved: "pill-green",
  completed: "pill-green",
  created: "pill-green",
  updated: "pill-green",
  live: "pill-green",
  active: "pill-green",
  unchanged: "pill-grey",
  stale_ignored: "pill-grey",
  completed_with_errors: "pill-yellow",
  rejected: "pill-red",
  revoked: "pill-red",
  failed: "pill-red",
  expired: "pill-red",
};

export const pillClass = (status) => `pill ${PILL[status] || "pill-grey"}`;

export const humanize = (slug) => (slug ? String(slug).replace(/_/g, " ") : "-");

export const formatDate = (value) =>
  value ? moment(value).tz("Europe/Amsterdam").format("DD-MM-YYYY HH:mm") : "-";

export const formatPercent = (rate) => (rate === null || rate === undefined ? "-" : `${(rate * 100).toFixed(1)}%`);

export const buildQuery = (params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== "" && value !== undefined && value !== null && value !== false) {
      query.append(key, value);
    }
  });
  return query.toString();
};

/**
 * Error text for a Dealer API admin call. Module errors come as the house envelope with a
 * stable data.code ({success:false, message, data:{code, details}}) — APIError() would read
 * data as a field-error map and show one character — and field validation as Laravel's
 * {message, errors}.
 */
export const dealerApiError = (error) => {
  const body = error?.response?.data;
  if (body && typeof body === "object" && !("exception" in body)) {
    if (body.errors && typeof body.errors === "object") {
      const first = Object.values(body.errors)[0];
      if (Array.isArray(first) && first.length) return first[0];
    }
    if (body.data && typeof body.data === "object" && typeof body.data.code === "string") {
      return body.message ? `${body.message} (${body.data.code})` : body.data.code;
    }
  }
  return error?.response ? APIError(error) : error?.message || "Something went wrong!";
};

export const toastError = (error) =>
  toast.error(dealerApiError(error), { position: toast.POSITION.TOP_RIGHT });

export const toastSuccess = (message) =>
  toast.success(message, { position: toast.POSITION.TOP_RIGHT });

/**
 * Permission flags of the `dealer_api` admin feature (read / add / update). The permissions are
 * written to localStorage at login, so they are stable for the page's lifetime.
 */
export const dealerApiAccess = () => {
  const feature = (JSON.parse(localStorage.getItem("permissions")) || []).find((o) => o.slug === "dealer_api");
  const can = (slug) => !!feature?.permissions?.find((o) => o.slug === slug);

  return { canRead: can("read"), canAdd: can("add"), canUpdate: can("update") };
};
